import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  Heart,
  Loader2,
  Lock,
  MessageCircle,
  Phone,
  ShieldCheck,
  Trash2,
  User,
  X,
  Camera,
} from "lucide-react";
import { deleteUser, signOut } from "firebase/auth";
import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";
import { ref, listAll, deleteObject } from "firebase/storage";
import Navbar from "../../Landing/Navbar";
import { auth, db, storage } from "../../firebase";
import { sendOtp, verifyOtp } from "../../services/api";
import useSettingsProfile from "./useSettingsProfile";

const REASONS = [
  { id: "marriage_karanki", label: "Marriage fixed through Karanki", Icon: Heart },
  { id: "marriage_elsewhere", label: "Marriage fixed elsewhere", Icon: Heart },
  { id: "no_matches", label: "Not finding good matches", Icon: User },
  { id: "privacy", label: "Privacy concerns", Icon: Lock },
  { id: "notifications", label: "Too many notifications", Icon: Phone },
  { id: "difficult", label: "Found the app difficult to use", Icon: AlertTriangle },
  { id: "other", label: "Other reason", Icon: MessageCircle },
];

const DELETED_ITEMS = [
  { Icon: User, label: "Your profile and personal details" },
  { Icon: Camera, label: "All uploaded photos" },
  { Icon: Heart, label: "Sent and received interests" },
  { Icon: Lock, label: "Login credentials and sessions" },
];

export default function DeleteAccountSettings() {
  const navigate = useNavigate();
  const { user, profile } = useSettingsProfile();
  const [step, setStep] = useState(1); // 1=reason, 2=otp, 3=confirm
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const phone = profile?.mobileNumber || JSON.parse(localStorage.getItem("karankiAuth") || "null")?.phone || "";

  const handleSendOtp = async () => {
    if (!phone) {
      setOtpError("Could not find your registered phone number.");
      return;
    }
    setOtpSending(true);
    setOtpError("");
    try {
      const result = await sendOtp(phone);
      setSessionId(result.sessionId);
      setOtpSent(true);
    } catch (err) {
      setOtpError(err.message || "Could not send OTP. Try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 4) {
      setOtpError("Enter the complete 4-digit OTP.");
      return;
    }
    setVerifying(true);
    setOtpError("");
    try {
      await verifyOtp(sessionId, otp, phone);
      setOtpVerified(true);
      setStep(3);
    } catch (err) {
      setOtpError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;
    if (!user) return;
    setDeleting(true);
    setError("");
    try {
      // ── 1. Delete all photos from Storage ─────────────────────────────
      try {
        const photosRef = ref(storage, `profile-images/${user.uid}`);
        const photosList = await listAll(photosRef);
        await Promise.all(photosList.items.map((item) => deleteObject(item)));
      } catch {
        // Storage folder might not exist or be empty — continue
      }

      // ── 2. Delete interests sent by or received by this user ──────────
      try {
        const [sentSnap, receivedSnap] = await Promise.all([
          getDocs(query(collection(db, "interests"), where("fromUserId", "==", user.uid))),
          getDocs(query(collection(db, "interests"), where("toUserId", "==", user.uid))),
        ]);
        const interestDocs = [...sentSnap.docs, ...receivedSnap.docs];
        await Promise.all(interestDocs.map((d) => deleteDoc(d.ref)));
      } catch {
        // interests may not exist — continue
      }

      // ── 3. Delete profile views where user is viewer or viewed ────────
      try {
        const [viewerSnap, viewedSnap] = await Promise.all([
          getDocs(query(collection(db, "profileViews"), where("viewerId", "==", user.uid))),
          getDocs(query(collection(db, "profileViews"), where("viewedId", "==", user.uid))),
        ]);
        const viewDocs = [...viewerSnap.docs, ...viewedSnap.docs];
        await Promise.all(viewDocs.map((d) => deleteDoc(d.ref)));
      } catch {
        // profileViews may not exist — continue
      }

      // ── 4. Delete reports filed by or about this user ─────────────────
      try {
        const [bySnap, aboutSnap] = await Promise.all([
          getDocs(query(collection(db, "reports"), where("reportedBy", "==", user.uid))),
          getDocs(query(collection(db, "reports"), where("reportedUserId", "==", user.uid))),
        ]);
        const reportDocs = [...bySnap.docs, ...aboutSnap.docs];
        await Promise.all(reportDocs.map((d) => deleteDoc(d.ref)));
      } catch {
        // reports may not exist — continue
      }

      // ── 5. Delete profile and user Firestore documents ────────────────
      await deleteDoc(doc(db, "profiles", user.uid)).catch(() => {});
      await deleteDoc(doc(db, "users", user.uid)).catch(() => {});

      // ── 6. Delete Firebase Auth user ─────────────────────────────────
      await deleteUser(user);

      // ── 7. Clear local storage ────────────────────────────────────────
      localStorage.removeItem("karankiAuth");
      localStorage.removeItem("karankiProfilePhone");
      localStorage.removeItem("karankiCreateProfileDraft");
      localStorage.removeItem("karankiCreateProfileStep");

      // Navigate to home
      navigate("/");
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        // If re-auth needed, sign out and redirect to login
        await signOut(auth).catch(() => {});
        localStorage.removeItem("karankiAuth");
        navigate("/login");
      } else {
        setError(err.message || "Could not delete account. Please try again.");
      }
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="page-container pt-4 pb-app-nav sm:pt-6 lg:pt-8">
        {/* Header */}
        <div className="mb-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => step > 1 ? setStep(step - 1) : navigate("/settings")}
            className="btn-icon h-10 w-10"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-ink-800">Delete Account</h1>
            <p className="text-[12.5px] text-ink-400">Step {step} of 3</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6 flex gap-1.5">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition ${
                s <= step ? "bg-rose-500" : "bg-sand-200"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: Reason */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="card p-5">
                <h2 className="font-display text-lg font-bold text-ink-800">Why are you leaving?</h2>
                <p className="mt-1 text-[13px] text-ink-400">
                  Your feedback helps us improve. Please select a reason.
                </p>

                <div className="mt-4 space-y-2">
                  {REASONS.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setReason(r.id)}
                      className={`flex w-full items-center gap-3 rounded-2xl border-2 p-3.5 text-left transition ${
                        reason === r.id
                          ? "border-rose-500 bg-rose-50"
                          : "border-sand-200 bg-white hover:border-rose-200"
                      }`}
                    >
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                        reason === r.id ? "bg-rose-100 text-rose-600" : "bg-sand-100 text-ink-400"
                      }`}>
                        <r.Icon size={16} />
                      </div>
                      <span className="flex-1 text-[13.5px] font-semibold text-ink-700">{r.label}</span>
                      {reason === r.id && (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white">
                          <Check size={12} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {reason === "other" && (
                  <textarea
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Please tell us why..."
                    rows={3}
                    className="input mt-3 resize-none"
                  />
                )}

                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!reason}
                  className="btn-primary mt-5 w-full py-3.5 disabled:opacity-50"
                >
                  Continue <ChevronRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 2: OTP Verification */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="card p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
                    <ShieldCheck size={22} />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-bold text-ink-800">Verify your identity</h2>
                    <p className="text-[12.5px] text-ink-400">We'll send an OTP to confirm it's you</p>
                  </div>
                </div>

                {/* What will be deleted */}
                <div className="mt-5 rounded-2xl border border-rose-100 bg-rose-50/50 p-4">
                  <p className="text-[12px] font-bold uppercase tracking-wider text-rose-600">What will be deleted</p>
                  <div className="mt-3 space-y-2.5">
                    {DELETED_ITEMS.map(({ Icon, label }) => (
                      <div key={label} className="flex items-center gap-2.5 text-[13px] text-rose-700">
                        <Icon size={14} className="shrink-0 text-rose-400" />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* OTP section */}
                <div className="mt-5">
                  {!otpSent ? (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={otpSending}
                      className="btn-primary w-full py-3.5"
                    >
                      {otpSending ? (
                        <><Loader2 size={16} className="animate-spin" /> Sending OTP...</>
                      ) : (
                        <><Phone size={16} /> Send OTP to {phone ? `***${phone.slice(-4)}` : "your number"}</>
                      )}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-[13px] font-semibold text-emerald-600">
                        ✓ OTP sent to ***{phone.slice(-4)}
                      </p>
                      <div>
                        <label className="field-label">Enter OTP</label>
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          placeholder="Enter 4-digit OTP"
                          inputMode="numeric"
                          maxLength={4}
                          className="input text-center text-lg font-bold tracking-[0.3em]"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={verifying || otp.length < 4}
                        className="btn-primary w-full py-3.5 disabled:opacity-50"
                      >
                        {verifying ? (
                          <><Loader2 size={16} className="animate-spin" /> Verifying...</>
                        ) : (
                          <><ShieldCheck size={16} /> Verify & Continue</>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {otpError && (
                  <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-[12.5px] font-semibold text-rose-700">
                    {otpError}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* STEP 3: Final confirmation */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="card p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                    <AlertTriangle size={28} />
                  </div>
                  <h2 className="mt-4 font-display text-xl font-bold text-ink-800">Final confirmation</h2>
                  <p className="mt-2 text-[13.5px] leading-6 text-ink-500">
                    This action is <span className="font-bold text-rose-600">permanent and cannot be undone</span>.
                    All your data including profile, photos, chats, and interests will be permanently deleted.
                  </p>
                </div>

                <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                    <div className="text-[12.5px] leading-5 text-amber-800">
                      <p className="font-semibold">Before you delete:</p>
                      <ul className="mt-1 list-disc pl-4 space-y-0.5">
                        <li>Your profile will be removed from all matches</li>
                        <li>Active premium membership will NOT be refunded</li>
                        <li>You cannot recover your account after deletion</li>
                        <li>You can create a new account with the same number later</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="field-label">Type <span className="font-bold text-rose-600">DELETE</span> to confirm</label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                    placeholder="Type DELETE here"
                    className="input text-center text-lg font-bold tracking-wider"
                  />
                </div>

                {error && (
                  <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-[12.5px] font-semibold text-rose-700">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={confirmText !== "DELETE" || deleting}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-rose-600 px-5 py-3.5 text-[14px] font-semibold text-white shadow-soft transition hover:bg-rose-700 active:scale-[0.98] disabled:opacity-50"
                >
                  {deleting ? (
                    <><Loader2 size={16} className="animate-spin" /> Deleting account...</>
                  ) : (
                    <><Trash2 size={16} /> Permanently delete my account</>
                  )}
                </button>

                <p className="mt-3 text-center text-[11.5px] text-ink-400">
                  This will sign you out and delete all data immediately.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
